<script setup lang='ts'>
import { isClient, numFormat, timeFrom } from '@qctsw/utils'
import { APP_ROUTER, addHost, imageTransform } from '@qctsw/common'
import { collectiveStep, useCollectiveDetail } from '../hooks/collective'
import CollectiveImages from '../components/CollectiveImages.vue'
import CollectiveSubComplaint from '../components/CollectiveSubComplaint.vue'
import RelativeCollective from '../components/RelativeCollective.vue'
import statistics from '../components/statistics.vue'
import { logicCollectiveDetailAllImage } from '~/logic/complaint/collective'

const dataType = 'collective'

const {
  id,
  state,
  detailData,
  tableData,
  brandId,
  seriesId,
  brandList,
  brandStrs,
  seriesStrs,
  initHandler,
} = useCollectiveDetail()

const {
  data: imgData,
  pagination,
  getPage,
  state: imgState,
} = logicCollectiveDetailAllImage()

useActivated({
  activatedFn: () => {
    useCommonFavoriteOrCollectEvent().reset()
    useCommonFavoriteOrCollectEvent().on((data) => {
      if (!detailData.value)
        return
      if (data.type === 'favorite') {
        detailData.value.good = !detailData.value.good
        detailData.value.goodCount += detailData.value.good ? 1 : -1
      }
    })
  },
  deactivatedFn: () => {
    useCommonFavoriteOrCollectEvent().reset()
  },
})

const { qrcode, getQrcode } = useMiniProgramQrcode()
if (isClient)
  getQrcode({ page: 'subpages/pages/complaint/detail-collective', scene: `${id.value}`, envVersion: 'release' })

await initHandler()
</script>

<template>
  <div bg-slate-1 pt-3>
    <div public-container bg-white p-3 relative>
      <n-popover placement="bottom" trigger="hover">
        <template #trigger>
          <div class="absolute right-0 top-0    w-10 h-10">
            <span class="i-grommet-icons:qr text-9 color-primary   " />
            <div class=" triangle absolute z-1 top-0" />
          </div>
        </template>
        <img w-30 h-30 :src="qrcode">
      </n-popover>

      <div flex justify-between gap-10>
        <div class="flex items-center flex-1">
          <n-avatar
            :style="{
              color: 'yellow',
              backgroundColor: 'rgb(59 130 246 / var(--un-bg-opacity))',

            }"
            :size="80"
          >
            {{ brandList[0]?.brandName }}
          </n-avatar>
          <div v-if="detailData" ml-3>
            <h2 text-title text-5 font-bold>
              {{ detailData.title }}
            </h2>
            <p text-3.5 color-slate-5>
              {{ detailData.seriesLs.map(i => i.seriesName).join("、") }}
            </p>
          </div>
        </div>

        <div flex gap-8 mr-8>
          <div class="flex-center flex-col">
            <p color-primary text-5 font-bold>
              {{ timeFrom(detailData?.createTime) }}
            </p>
            <p class="text-3.5 color-slate-5">
              最新投诉
            </p>
          </div>
          <div v-if="detailData?.goodCount" class="flex-center flex-col">
            <p color-primary text-5 font-bold>
              {{ detailData?.goodCount }}
            </p>
            <p class="text-3.5 color-slate-5">
              点赞
            </p>
          </div>
          <div class="flex-center flex-col">
            <p color-primary text-5 font-bold>
              {{ `${numFormat(detailData?.lookCount || 0)}${detailData?.lookCount || 0 > 10000 ? '+' : ''}` }}
            </p>
            <p class="text-3.5 color-slate-5">
              浏览
            </p>
          </div>
        </div>
      </div>
      <div bg-slate-1 mt-3 p-3 rounded>
        <BasicDetailTable
          item-width="100%"
          :table-data="tableData"
        />
      </div>

      <div v-if="detailData?.complainScore?.length" class=" mt-4 rounded  flex ">
        <div class=" bg-slate-1 px-1 flex-center tracking-widest py-3 text-3.5 font-400 " style="writing-mode:vertical-rl">
          服务评价
        </div>
        <div class="flex-1 px-5  overflow-hidden">
          <NMarquee>
            <div class="flex gap-5 w-full ">
              <NuxtLink v-for="i, index in detailData.complainScore" :key="i.complainId" target="_blank" :to="`/complaint/detail/${i.complainId}`" class="block " :class="index === 0 ? 'ml-5' : ''">
                <NCard class="w-70 h-full" size="small">
                  <div class="flex items-center gap-1">
                    <n-avatar

                      :style="{

                        backgroundColor: '#3b82f6',
                      }"
                    >
                      {{ i.carUserName }}
                    </n-avatar>
                    {{ i.userPhone }}
                    <n-rate size="small" readonly :default-value="i.complainScoreCode + 3" />
                  </div>
                  <p mt-1 line-clamp-2 text-3.5 color-slate-5>
                    {{ i.scoreRemark }}
                  </p>
                </NCard>
              </NuxtLink>
            </div>
          </NMarquee>
        </div>
      </div>
    </div>

    <div class="public-container public-page flex ">
      <div class="flex-grow min-w-0 relative">
        <UtilLoadState
          :state="state"
          :reload-handler="() => initHandler()"
        >
          <div v-if="detailData" class="p-sm rounded-md bg-white">
            <n-tabs type="line" animated>
              <n-tab-pane name="oasis" tab="数据概况">
                <div class="step flex items-center mt-6 rounded-1.5 shadow overflow-hidden border-1 border-slate-1">
                  <div
                    class="flex-shrink-0 flex items-center justify-center write-vertical-left w-15 text-white bg-blue-5 text-6 min-h-30"
                  >
                    进度
                  </div>
                  <div class="flex-grow min-w-0 overflow-hidden pr-6">
                    <BasicStep
                      :custom="collectiveStep"
                      :active="detailData.progressCode"
                      top="-12px"
                      width="100px"
                    >
                      <template #default="{ item, index }">
                        <div
                          class="step-item"
                          :class="{ 'is-active': index <= detailData.progressCode }"
                        >
                          {{ item.title }}
                        </div>
                      </template>
                    </BasicStep>
                  </div>
                </div>
                <!-- <ClientOnly> -->
                <statistics :id="id" />
                <RelativeCollective
                  :brand-list="brandList"
                  :series-list="detailData.seriesLs"
                  :complain-explain="detailData.complainExplain"
                  :measure="detailData.measure"
                  :newest-result="{
                    newestResultExplain: detailData.newestResultExplain,
                    collectiveResultScoreCode: detailData.collectiveResultScoreCode,
                  }"
                />
                <!-- </ClientOnly> -->
              </n-tab-pane>
              <n-tab-pane name="the beatles" tab="图片">
                <div class="image-box">
                  <n-image-group>
                    <n-image
                      v-for="(item, index) in [...detailData.complainImgs, ...imgData]"
                      :key="index" width="100%"
                      :src="imageTransform(item)"
                    />
                  </n-image-group>
                </div>

                <BasicLoadButton
                  class="bg-slate-2"
                  :state="imgState"
                  @load="() => getPage(pagination.pagination.page, { collectiveId: id })"
                >
                  <!-- <span v-if="isGoListPage">
              <span>查看更多</span>
              <i class="i-ic:round-keyboard-double-arrow-right vertical-icon text-3.5" />
            </span>
            <span v-else> 加载更多</span> -->
                </BasicLoadButton>
              </n-tab-pane>
              <n-tab-pane v-if="detailData.complainVideos.length" name="jay chou" tab="视频">
                <video
                  v-for="v in detailData.complainVideos"
                  :key="v.videoUrl"
                  :poster="imageTransform(v.complainImgUrl, { height: 462, width: 820 })
                  "
                  :src="addHost(v.videoUrl)"
                  :autoplay="false"
                  controlslist="nodownload"
                  controls
                  class="w-full h-full m-3"
                />
              </n-tab-pane>
              <n-tab-pane v-if="detailData.complainAudios.length" name="jay chou1" tab="音频">
                <div
                  v-for="v in detailData.complainAudios"
                  :key="v"
                  class="flex-center h-full m-3"
                >
                  <audio
                    controls
                    class="w-3/4"
                    :src="addHost(v)"
                  />
                </div>
              </n-tab-pane>
            </n-tabs>
            <div class="bg-slate-1 text-3.25 p-xs rounded text-slate-5 mt-sm indent-2em">
              免责声明：站内会员言论仅代表个人观点，并不代表汽车投诉网[QcTsw.Com]同意其说法或描述，不承担相关法律责任。未经投诉人及汽车投诉网共同授权许可，任何单位与个人不得转载，如需转载请联系!
            </div>
            <UtilCommentOperation
              class="mt-xl"
              :is-good="detailData.good"
              :data-id="Number(id)"
              :data-type="dataType"
              :is-show-collect="false"
            />

            <!-- <template v-if="detailData">
              <div class="px-30 text-title text-6.5 font-bold text-center">
                {{ detailData.title }}
              </div>
              <div class="flex items-center justify-between px-sm mt-xl text-gray-5 text-3.5">
                <div class="flex items-center">
                  <NuxtLink
                    class="mr-sm"
                    :to="APP_ROUTER.complaintList({ type: 1 })"
                  >
                    集体投诉
                  </NuxtLink>
                  <div class="mr-6">
                    {{ timeFrom(detailData.createTime) }}
                  </div>
                  <UtilContentTips
                    size="14"
                    :is-good="detailData.good"
                    :data-id="parseInt(id ?? '-1')"
                    :data-type="dataType"
                    :can-set-good="true"
                    :tips-data="{
                      lookCount: detailData.lookCount ?? 0,
                      favorCount: detailData.goodCount ?? 0,
                    }"
                  />
                </div>
                <UtilMiniQrcode :qrcode="qrcode" />
              </div>
              <div class="mt-6 w-100% rounded-1.5">
                <BasicDetailTable
                  item-width="100%"
                  :table-data="tableData"
                />
              </div>
              <div class="step flex items-center mt-6 rounded-1.5 shadow overflow-hidden border-1 border-slate-1">
                <div
                  class="flex-shrink-0 flex items-center justify-center write-vertical-left w-15 text-white bg-blue-5 text-6 min-h-30"
                >
                  进度
                </div>
                <div class="flex-grow min-w-0 overflow-hidden pr-6">
                  <BasicStep
                    :custom="collectiveStep"
                    :active="detailData.progressCode"
                    top="-12px"
                    width="100px"
                  >
                    <template #default="{ item, index }">
                      <div
                        class="step-item"
                        :class="{ 'is-active': index <= detailData.progressCode }"
                      >
                        {{ item.title }}
                      </div>
                    </template>
                  </BasicStep>
                </div>
              </div>
              <CollectiveImages
                v-if="detailData.complainImgs.length"
                :pictures="detailData.complainImgs"
                :collective-id="id"
              />
              <NCarousel
                v-if="detailData.complainVideos.length"
                :loop="true"
                :show-dots="false"
                centered-slides
                show-arrow
                dot-type="line"
                effect="fade"
                :autoplay="false"
                class="mt-6 bg-black rounded-md h-462px"
              >
                <video
                  v-for="v in detailData.complainVideos"
                  :key="v.videoUrl"
                  :poster="imageTransform(v.complainImgUrl, { height: 462, width: 820 })
                  "
                  :src="addHost(v.videoUrl)"
                  :autoplay="false"
                  controlslist="nodownload"
                  controls
                  class="w-full h-full"
                />
              </NCarousel>

              <NCarousel
                v-if="detailData.complainAudios.length"
                loop
                :show-dots="false"
                centered-slides
                show-arrow
                dot-type="line"
                effect="fade"
                :autoplay="false"
                class="mt-6 bg-gray-2 rounded-md h-100px audio"
              >
                <div
                  v-for="v in detailData.complainAudios"
                  :key="v"
                  class="flex-center h-full"
                >
                  <audio
                    controls
                    class="w-3/4"
                    :src="addHost(v)"
                  />
                </div>
              </NCarousel>

              <UtilCommentOperation
                class="mt-xl"
                :is-good="detailData.good"
                :data-id="Number(id)"
                :data-type="dataType"
                :is-show-collect="false"
              />
            </template> -->
            <!-- <BasicEmpty

              text="未找到相关集体投诉"
            /> -->
          </div>
          <BasicEmpty
            v-else
            text="未找到相关集体投诉"
          />
          <div
            v-if="detailData"
            class="p-sm rounded-md bg-white mt-sm"
          >
            <CollectiveSubComplaint
              :data-id="id"
              :brand-strs="brandStrs"
              :series-strs="seriesStrs"
              :complain-count="detailData.complainCount"
            />
          </div>
          <ClientOnly>
            <div
              v-if="detailData"
              class="bg-white rounded-md mt-lg p-sm"
            >
              <LayoutComment
                :data-id="Number(id)"
                :data-type="dataType"
              />
            </div>
          </ClientOnly>
          <div class="fixed">
            <NAffix :top="300">
              <NuxtLink
                target="_blank"
                :to="APP_ROUTER.publish({ collectiveId: id, brandId, seriesId })"
              >
                <div class="fixed-container flex items-center justify-center text-3.75 font-bold">
                  点击参与集体投诉
                </div>
              </NuxtLink>
            </NAffix>
          </div>
        </UtilLoadState>
      </div>
      <div class="flex-shrink-0 w-[calc(100%-53rem)] pl-xl">
        <ClientOnly>
          <RankHotCollective
            class="mb-xl"
            :embedded="false"
          />
          <RankHotComplaint
            class="mb-xl"
            :embedded="false"
          />
          <RankRelativeComplaint
            class="sticky top-25"
            :series-id="seriesId"
            :embedded="false"
          />
        </ClientOnly>
      </div>
    </div>
  </div>
</template>

<style lang='scss' scoped>
.step-item {
  --uno: px-4 leading-10 text-center bg-slate-3 text-white rounded-full whitespace-nowrap font-bold;

  &.is-active {
    background: linear-gradient(180deg, #6ad5e5 0%, #478afa 100%);
  }
}

.n-carousel {

  &:hover{
    &.n-carousel--bottom {

      :deep(.n-carousel__arrow-group) {
      display: block;
    }
    }

  }
  &.n-carousel--bottom :deep(.n-carousel__arrow-group) {
    position: static;
    display: none;

    .n-carousel__arrow {

      --uno: bg-black bg-opacity-40 h-100px text-10 w-10  ;
      position: absolute;
      right: 0;
      top:50%;
      transform: translateY(-50%);
      &:last-child{
        border-radius: 10px 0 0px 10px;
      }

      &:first-child {
        border-radius: 0px 10px 10px 0px;
        left: 0;
      }

    }
  }

}

.n-carousel.n-carousel--bottom :deep(.n-carousel__arrow-group) {
  --uno: bottom-15;

}

.fixed {
  position: absolute;
  top: 0;
  left: -65px;

  &-container {
    --uno: bg-blue-5 w-12 py-sm min-h-50 rounded-md overflow-hidden cursor-pointer;
    --uno: tracking-1.5 text-5 text-white write-vertical-left;
  }
}
.image-box {
  column-count: 3;
  column-gap: 10px;
}
.n-marquee:hover{
  --n-play:paused !important;

}
.triangle {
    width: 0;
    height: 0;
    border-bottom: 45px solid #fff;
    border-right: 45px solid transparent;
}
</style>
